<template>
    <Portal to="sidepanel">
        <SidePanel
            key="editExamSidePanel"
            :name="sidePanelName"
            class="edit-exam"
            width="medium"
            :tabs="tabs"
            :active-tab-name="activeTab"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
            @selectTab="selectTab"
        >
            <Portal to="modal">
                <ResetProgressModal
                    v-if="showResetProgressModal"
                    :is-loading="isResetLoading"
                    @resetProgress="resetProgress"
                    @close="showResetProgressModal = false"
                />
                <ModalContainer
                    v-if="showCannotDeleteModal"
                    key="cannotDeleteExamModal"
                    :is-dark-mode="isDarkMode"
                    @close="showCannotDeleteModal = false"
                >
                    <template #modal>
                        <Modal
                            :show-close-button="true"
                            class="edit-exam__modal"
                            :is-dark-mode="isDarkMode"
                            @close="showCannotDeleteModal = false"
                        >
                            <div class="edit-exam__modal-title">
                                Cannot Delete Exam
                            </div>
                            <div v-dark class="edit-exam__modal-message">
                                This is your only exam, thus you cannot delete it. If you would like to cancel your
                                subscription, go to Settings > Purchases. If you would like to delete your account, go
                                to Settings > Account.
                            </div>
                            <div class="edit-exam__modal-buttons">
                                <PocketButton
                                    class="edit-exam__modal-button"
                                    :is-dark-mode="isDarkMode"
                                    @click="showCannotDeleteModal = false"
                                >
                                    Got it
                                </PocketButton>
                            </div>
                        </Modal>
                    </template>
                </ModalContainer>
            </Portal>
            <Portal to="modal">
                <ModalContainer
                    v-if="showDeleteConfirm"
                    key="confirmDeleteExamModal"
                    :is-dark-mode="isDarkMode"
                    @close="showDeleteConfirm = false"
                >
                    <template #modal>
                        <Modal
                            :show-close-button="true"
                            class="edit-exam__modal"
                            :is-dark-mode="isDarkMode"
                            @close="showDeleteConfirm = false"
                        >
                            <div class="edit-exam__modal-title">
                                Delete {{ examMetadata && examMetadata.nativeAppName }} Exam?
                            </div>
                            <div v-dark class="edit-exam__modal-message">
                                This will delete all of your progress. It is irreversible. Are you sure you want to
                                delete?
                            </div>
                            <div class="edit-exam__modal-buttons">
                                <PocketButton
                                    class="edit-exam__modal-button"
                                    type="secondary"
                                    :is-dark-mode="isDarkMode"
                                    @click="showDeleteConfirm = false"
                                >
                                    Never mind
                                </PocketButton>
                                <PocketButton
                                    type="primary-red"
                                    class="edit-exam__modal-button"
                                    :is-loading="isLoading"
                                    :is-dark-mode="isDarkMode"
                                    @click="deleteExam"
                                >
                                    Yes, Delete
                                </PocketButton>
                            </div>
                        </Modal>
                    </template>
                </ModalContainer>
            </Portal>
            <template v-if="activeTab === 'General'">
                <div v-dark class="edit-exam__note">
                    To edit your subscription, go to
                    <RouterLink
                        v-slot="{ href, navigate }"
                        custom
                        :to="{ name: 'purchases' }"
                    >
                        <PocketLink
                            class="edit-exam__link"
                            type="tertiary-small"
                            :href="href"
                            :is-dark-mode="isDarkMode"
                            @click="navigate"
                            @mousedown.prevent
                        >
                            <span>Purchases</span>
                        </PocketLink>
                    </RouterLink>.
                </div>
                <div class="edit-exam__progress-top">
                    <div class="edit-exam__progress-top-title">
                        Study Progress
                    </div>
                    <PocketLink
                        v-if="totalAnswers"
                        type="tertiary-small"
                        :disabled="isLoading"
                        :is-dark-mode="isDarkMode"
                        @click="showResetProgressModal = true"
                    >
                        Reset Progress
                    </PocketLink>
                </div>
                <div v-dark class="edit-exam__progress">
                    <div v-dark class="edit-exam__progress-container">
                        <div
                            v-dark
                            class="edit-exam__progress-bar"
                            :style="{ width: `${progressPercent}%` }"
                        />
                    </div>
                    <div v-dark class="edit-exam__progress-percent">
                        {{ (!progressPercent || isNaN(Number(progressPercent))) ? '0.0' : progressPercent }}%
                    </div>
                </div>
            </template>
            <template v-if="activeTab === 'Prep Content' && !isLoading">
                <div
                    v-if="currentExamVersion && examVersions.length > 1 && !hasSeenLatest && !isLoading"
                    v-dark
                    class="edit-exam__review-version-card"
                    tabindex="0"
                >
                    <div class="edit-exam__review-version-card-header">
                        <div v-dark class="edit-exam__review-version-card-name">
                            {{ currentExamVersion.nativeAppName }}
                        </div>
                        <PocketLink
                            v-dark
                            class="edit-exam__review-version-card-link"
                            @click="showReviewNewVersions = true"
                            @mousedown.prevent
                        >
                            Review Versions
                        </PocketLink>
                    </div>
                    <div v-dark class="edit-exam__review-version-card-info">
                        Version {{ currentExamVersion.version }}
                    </div>
                    <div v-dark class="edit-exam__review-version-card-description">
                        <Icon class="edit-exam__review-version-card-icon" type="warning" />
                        New Exam Version Available
                    </div>
                </div>
                <div v-if="(examVersions.length > 1 && hasSeenLatest && !isLoading) ||
                         (examVersions.length === 1 && !isLoading)"
                     class="edit-exam__versions"
                >
                    <ExamVersionCard
                        v-if="currentExamVersion"
                        :key="currentExamVersion.objectId"
                        :exam="currentExamVersion"
                        :is-selected-version="
                            !!selectedMajorVersion && currentExamVersion.version.startsWith(`${selectedMajorVersion}.`)
                        "
                        :is-active-exam="currentExamVersion.version.startsWith(`${originalMajorVersion}.`)"
                        :is-latest-version="selectedMajorVersion === currentMajorVersion"
                        :hasMultipleVersions="activeExamVersions.length > 1"
                        :is-dark-mode="isDarkMode"
                        @clickExamVersion="clickExamVersion"
                        @clickReviewExamVersions="showReviewNewVersions = true"
                    />
                </div>
            </template>
            <template v-if="activeTab === 'Exam Date'">
                <div class="edit-exam__label">
                    Set your exam date.
                </div>
                <div v-dark class="edit-exam__p">
                    We'll help you count down and send you encouragement on exam day!
                </div>
                <Calendar
                    v-model="examDate"
                    label="Scheduled Exam Date"
                    class="edit-exam__exam-date"
                    :is-dark-mode="isDarkMode"
                />
            </template>
            <template #details>
                <PocketButton
                    :disabled="isLoading"
                    type="secondary"
                    class="edit-exam__delete"
                    :is-dark-mode="isDarkMode"
                    @click="clickDelete"
                >
                    Delete Exam
                </PocketButton>
            </template>
            <template #action>
                <PocketButton
                    :disabled="originalExamDate === examDate"
                    :is-loading="isLoading"
                    class="edit-exam__save--desktop"
                    :is-dark-mode="isDarkMode"
                    @click="saveSettings"
                >
                    Save Settings
                </PocketButton>
                <PocketButton
                    :disabled="originalExamDate === examDate"
                    :is-loading="isLoading"
                    class="edit-exam__save--mobile"
                    :is-dark-mode="isDarkMode"
                    @click="saveSettings"
                >
                    Save
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
    <ReviewNewVersionsSidePanel
        v-if="showReviewNewVersions && currentExamVersion"
        key="study"
        :passed-exam-versions="examVersions"
        :current-exam-version="currentExamVersion"
        :uem="userExamMetadataForExamGuid"
        :exam-metadata="examMetadata"
        @close="showReviewNewVersions = false"
    />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { progressModule } from '@/store/progress/module'
import { questionModule } from '@/store/question/module'
import { quizModule } from '@/store/quiz/module'
import { subscriptionModule } from '@/store/subscription/module'
import { toastModule } from '@/store/toast/module'
import { userModule } from '@/store/user/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import UIKit from '@pocketprep/ui-kit'
import ResetProgressModal from '@/components/Stats/ResetProgressModal.vue'
import { bundleModule } from '@/store/bundle/module'
import ExamVersionCard from '@/components/Settings/EditExam/ExamVersionCard.vue'
import type { Study } from '@pocketprep/types'
import type { ClientExam } from '@/store/bundle/state'
import ReviewNewVersionsSidePanel from '@/components/ReviewNewVersionsSidePanel.vue'

const {
    SidePanel,
    Button: PocketButton,
    Link: PocketLink,
    Calendar,
    ModalContainer,
    Modal,
    Icon,
} = UIKit

const props = withDefaults(
    defineProps<{
        examId: string
        passedActiveTab?: 'General' | 'Prep Content' | 'Exam Date' | null
    }>(),
    {
        passedActiveTab: null,
    }
)

const emit = defineEmits<{
    close: [value: boolean]
}>()

const examDate = ref<Date | null>(null)
const originalExamDate = ref<Date | null>(null)
const selectedMajorVersion = ref<string | null>(null)
const currentMajorVersion = ref<string | null>(null)
const originalMajorVersion = ref<string | null>(null)
const isLoading = ref(true)
const isResetLoading = ref(false)
const activeTab = ref('General')
const showDeleteConfirm = ref(false)
const showCannotDeleteModal = ref(false)
const showResetProgressModal = ref(false)
const showReviewNewVersions = ref(false)
const uem = ref<Study.Class.UserExamMetadataJSON | undefined>(undefined)
const tabs = [
    'General',
    'Prep Content',
    'Exam Date',
]

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const sidePanelName = computed(() => {
    return examMetadata.value
        ? `${examMetadata.value.nativeAppName} Settings`
        : 'Exam Settings'
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()[props.examId]
})

const userExamMetadataForExamGuid = computed(() => {
    return examMetadata.value
        && userExamMetadataModule.getters.getUserExamMetadataForExamGuid(examMetadata.value.examGuid)
})

const currentUserExamMetadata = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const examBundle = computed(() => {
    return bundleModule.getters.getBundles().find(b => b.exams.find(e => e.objectId === props.examId))
})

const examVersions = computed(() => {
    return examMetadataModule.getters.getExamMetadata()
        .filter(e =>
            e.examGuid === userExamMetadataForExamGuid.value?.examGuid
            && (
                examBundle.value?.exams.find(be => e.objectId === be.objectId)
                || userExamMetadataForExamGuid.value?.examVersion?.startsWith(`${e.version.split('.')[0]}.`)
            )
        )
        .sort((a, b) =>
            a.objectId === userExamMetadataForExamGuid.value?.objectId
                ? 1
                : -a.version.localeCompare(b.version, undefined, { numeric: true })
        )
})

const activeExamVersions = computed(() => {
    return examVersions.value.filter(exam => !examMetadataModule.getters.getIsExamDiscontinued(exam))
})

const currentExamVersion = computed(() => {
    const examMajorVersion = userExamMetadataForExamGuid.value?.examVersion?.split('.')[0]
    return examVersions.value.find(e => e.version.split('.')[0] === examMajorVersion)
})

const progressByExamGuid = computed(() => {
    return progressModule.getters.getProgressByExamGuid()
})

const totalAnswers = computed(() => {
    const questionFilter = hasSubscription.value ? 'all' : 'free'
    return currentUserExamMetadata.value?.examGuid === examMetadata.value?.examGuid
        ? Object.keys(quizModule.getters.getLatestAnswers({ questionFilter })).length
        : progressByExamGuid.value[examMetadata.value?.examGuid]?.answered || 0
})

const hasSubscription = computed(() => {
    return !!subscriptionModule.getters.getSubscriptionForExamId(examMetadata.value?.objectId)
})

const totalQuestions = computed(() => {
    return currentUserExamMetadata.value?.examGuid === examMetadata.value?.examGuid
        ? hasSubscription.value
            ? (examMetadata.value?.itemCount || 0) - (examMetadata.value?.archivedCount || 0)
            : examMetadata.value?.specialQuestions || 0
        : progressByExamGuid.value[examMetadata.value?.examGuid]?.total || 0
})

const progressPercent = computed(() => {
    const totalQuestionsVal = totalQuestions.value
    const totalAnswersVal = totalAnswers.value
    const percent = (totalAnswersVal / totalQuestionsVal) * 100

    return Math.round(percent) === 100 ? 100 : percent.toPrecision(2)
})

const hasSeenLatest = computed(() => {
    if (examVersions.value && examVersions.value.length > 1) {
        // if user is on the exam's latest major version then they have seen the latest
        const examCurrentMajorVersion = examVersions.value[0].version.split('.')[0]
        const userCurrentMajorVersion = currentExamVersion.value?.version?.split('.')[0] || '1'
        if (userCurrentMajorVersion === examCurrentMajorVersion) {
            return true
        }
    }

    return false
})

onMounted(async () => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        userModule.actions.fetchUserData(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
    ])

    const scheduledExamDate = userExamMetadataForExamGuid.value?.scheduledExamDate
    examDate.value = scheduledExamDate ? new Date(scheduledExamDate.iso) : null
    originalExamDate.value = examDate.value
    const examMajorVersion = userExamMetadataForExamGuid.value?.examVersion?.split('.')[0]
    selectedMajorVersion.value = examMajorVersion ? examMajorVersion : null
    originalMajorVersion.value = selectedMajorVersion.value
    uem.value = userExamMetadataForExamGuid.value

    if (props.passedActiveTab) {
        activeTab.value = props.passedActiveTab
    }
    isLoading.value = false
})

const clickDelete = () => {
    if (userExamMetadataModule.getters.getUserExamMetadata().length <= 1) {
        showCannotDeleteModal.value = true
    } else {
        showDeleteConfirm.value = true
    }
}

const selectTab = (tab: string) => {
    activeTab.value = tab
}

const saveSettings = async () => {
    const uem = userExamMetadataForExamGuid.value // eslint-disable-line no-shadow -- preserve original variable name

    if (!uem) {
        throw new Error('No current userExamMetadata.')
    }

    isLoading.value = true
    const updatePromises: Promise<unknown>[] = []
    if (examDate.value?.getTime() !== originalExamDate.value?.getTime() && examBundle.value) {
        updatePromises.push(userExamMetadataModule.actions.setScheduledExamDate({
            scheduledExamDate: examDate.value || null,
            examNativeName: examMetadata.value.nativeAppName,
            uemCreatedAt: uem.createdAt,
            uemObjectId: uem.objectId,
            bundle: examBundle.value,
            hasSubscription: hasSubscription.value,
        }))
    }
    await Promise.all(updatePromises)

    if (examDate.value?.getTime() !== originalExamDate.value?.getTime()) {
        toastModule.actions.displayToast({
            title: 'Exam date saved successfully',
        })
    }

    emitClose()
    isLoading.value = false
}

const deleteExam = async () => {
    isLoading.value = true
    const uemToDelete = userExamMetadataForExamGuid.value
    const isDeletingCurrentExam =
        uemToDelete?.objectId === userExamMetadataModule.getters.getCurrentUserExamMetadata()?.objectId
    // if deleting current exam, switch to any new one
    if (isDeletingCurrentExam) {
        const newUEM = userExamMetadataModule.getters.getUserExamMetadata().find(
            userExamMetadata => userExamMetadata.objectId !== userExamMetadataForExamGuid.value?.objectId
        )
        if (!newUEM) {
            throw new Error('No new exam to switch to.')
        }
        const examMetadataForUEM = examMetadataModule.getters.getExamMetadataForUEM(newUEM)
        if (!examMetadataForUEM) {
            throw new Error('No new exam to switch to')
        }

        await userModule.actions.updateCurrentExam(examMetadataForUEM.objectId)
    }
    if (examMetadata.value) {
        await userExamMetadataModule.actions.resetExamProgress(examMetadata.value)
    }
    if (uemToDelete) {
        await userExamMetadataModule.actions.deleteUserExamMetadata(uemToDelete)
    }

    toastModule.actions.displayToast({
        title: `${examMetadata.value?.nativeAppName} deleted successfully`,
        subtext: 'If you would like to restart  this exam, you can re-add it at any time.',
    })
    emitClose()
    isLoading.value = false
}

const resetProgress = async () => {
    isResetLoading.value = true
    if (examMetadata.value) {
        await userExamMetadataModule.actions.resetExamProgress(examMetadata.value)
    }
    toastModule.actions.displayToast({
        title: 'Your progress was reset',
    })
    showResetProgressModal.value = false
    isResetLoading.value = false
}

const clickExamVersion = (exam: Study.Class.ExamMetadataJSON | ClientExam) => {
    selectedMajorVersion.value = exam.version.split('.')[0]
}

const emitClose = () => {
    emit('close', true)
}

const currentExamVersionChange = () => {
    selectedMajorVersion.value = currentExamVersion.value?.version.split('.')[0] || null
}

watch(currentExamVersion, currentExamVersionChange)

// watching when the review side panel open or closes
// to get most recent user data
const checkIfHasCurrentExamMetadata = async () => {
    isLoading.value = true
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        bundleModule.actions.fetchBundles(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
    ])
    isLoading.value = false
}

watch(showReviewNewVersions, checkIfHasCurrentExamMetadata)
</script>

<style lang="scss" scoped>
.edit-exam {
    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;
        width: 392px;
        padding: 35px 0 26px;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            max-height: 638px;
            top: 10px;
        }
    }

    &__modal-title {
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        width: 309px;
        margin: 0 auto 12px;
    }

    &__modal-message {
        font-size: 15px;
        line-height: 22px;
        color: $slate-03;
        margin-bottom: 29px;
        width: 309px;

        &--dark {
            color: $white;
        }
    }

    &__modal-buttons {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &__modal-button {
        margin: 0 6px;
    }

    &__delete {
        margin-left: -20px;
    }

    &__label {
        font-size: 15px;
        font-weight: 600;
        line-height: 18px;
        margin-bottom: 12px;
    }

    &__p {
        font-size: 14px;
        line-height: 18px;
        color: $slate-03;
        margin-bottom: 12px;

        &--dark {
            color: $pewter;
        }
    }

    &__warning {
        font-size: 15px;
        line-height: 18px;
        margin-bottom: 31px;
        color: $red-pegasus;

        &--dark {
            color: $rosa;
        }
    }

    &__exam-date {
        width: calc(100% + 24px);
        margin-left: -12px;
        margin-right: -12px;
        margin-top: 23px;
    }

    &__note {
        color: $slate-03;
        border-bottom: 1px solid $gray-divider;
        margin-bottom: 16px;
        font-size: 14px;
        line-height: 18px;
        padding: 0 12px 16px;
        box-sizing: border-box;
        width: calc(100% + 24px);
        margin-left: -12px;
        margin-right: -12px;

        &--dark {
            color: $pewter;
            border-bottom-color: $slate;
        }
    }

    &__link {
        text-decoration: none;
        font-weight: 500;
    }

    &__progress-top {
        display: flex;
        justify-content: space-between;
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 7px;
    }

    &__progress {
        width: calc(100% + 24px);
        height: 38px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: $white;
        border-radius: 6px;
        margin-left: -12px;
        margin-right: -12px;
        padding: 0 12px;
        box-sizing: border-box;

        &--dark {
            background: $charcoal;
        }
    }

    &__progress-container {
        width: 324px;
        height: 14px;
        background: $gray-progress-background;
        border-radius: 2px;
        margin-right: 13px;
        position: relative;

        &--dark {
            background: $moonlit-ocean;
        }

        @include breakpoint(black-bear) {
            width: 100%;
        }
    }

    &__progress-bar {
        position: absolute;
        left: 0;
        top: 0;
        height: 14px;
        border-radius: 2px;
        background: $brand-black;

        &--dark {
            background: $pewter;
        }
    }

    &__progress-percent {
        font-size: 15px;
        line-height: 15px;
    }

    &__save {
        &--mobile {
            display: none;

            @include breakpoint(black-bear) {
                display: block;
            }
        }

        &--desktop {
            display: block;

            @include breakpoint(black-bear) {
                display: none;
            }
        }
    }

    &__review-version-card {
        box-shadow: 0 1px 6px 0 rgba($gray-divider, 0.6);
        background: $white;
        border-radius: 5px;
        width: calc(100% + 24px);
        margin-left: -12px;
        margin-right: -12px;
        padding: 14px 15px;
        box-sizing: border-box;
        margin-bottom: 16px;
        position: relative;
        outline: none;

        &--dark {
            background-color: $moonlit-ocean;
            box-shadow: 0 1px 6px 0 rgba($charcoal, 0.3);
        }
    }

    &__review-version-card-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 6px;
    }

    &__review-version-card-name {
        font-size: 17px;
        line-height: 20px;
        font-weight: 600;
        color: $brand-black;
        width: 235px;

        &--dark {
            color: $fog;
        }
    }

    &__review-version-card-info {
        letter-spacing: -0.3px;
        color: $ash;
        font-size: 14px;
        line-height: 16px;
        text-align: left;
        margin-bottom: 13px;

        &--dark {
            color: $fog;
        }

        span {
            font-weight: 600;
        }
    }

    &__review-version-card-description {
        display: flex;
        color: $brand-black;
        font-size: 14px;
        line-height: 19px;
        text-align: left;

        &--dark {
            color: $white;
        }
    }

    &__review-version-card-icon{
        margin-right: 7px;
        margin-top: 2px;
    }
}
</style>
